import { useEffect } from "react"

import { CONVERSATION_SEARCH_TYPE, type ConversationSearchType } from "../types"

interface UseConversationsVisualizerPhantomSelectionClearParams {
  clusterFilterIds: string[] | null
  conversationCount: number
  isFetching: boolean
  searchType: ConversationSearchType
  selectedKeys: Set<string>
  setSelectedKeys: (keys: Set<string>) => void
  tableHasInitiallyLoaded: boolean
}

/**
 * A visualizer conversation-dot click optimistically selects the clicked id alongside
 * applying the exact-match filter, before the query confirms the conversation still
 * exists (see useConversationsVisualizerSelection). If it was deleted, or an access-scope
 * change excluded it, since the visualizer's own data was fetched, the query settles with
 * zero rows while selectedKeys still holds that phantom id -- bulk delete derives run ids
 * only from loaded rows, so it would silently have nothing to act on. SIMULATION_OUTPUT_ID
 * search is only ever entered via that click path, so this settles the phantom selection
 * without touching any other selection flow (manual search, cluster filter, restore).
 */
export function useConversationsVisualizerPhantomSelectionClear({
  clusterFilterIds,
  conversationCount,
  isFetching,
  searchType,
  selectedKeys,
  setSelectedKeys,
  tableHasInitiallyLoaded,
}: UseConversationsVisualizerPhantomSelectionClearParams) {
  useEffect(() => {
    if (
      tableHasInitiallyLoaded &&
      !isFetching &&
      conversationCount === 0 &&
      selectedKeys.size > 0 &&
      clusterFilterIds === null &&
      searchType === CONVERSATION_SEARCH_TYPE.SIMULATION_OUTPUT_ID
    ) {
      setSelectedKeys(new Set())
    }
  }, [
    clusterFilterIds,
    conversationCount,
    isFetching,
    searchType,
    selectedKeys,
    setSelectedKeys,
    tableHasInitiallyLoaded,
  ])
}
